import * as angular from "angular";
import {NavianceSchoolWithProducts} from "../models/school/naviance-school-with-products.model";
import {Column} from "../models/column.model";
import {Match} from "../models/match.model";
import {StaffImportStatus} from "../models/staff-import-status.model";

interface NavianceSchoolWithNameAndStatus {
    navianceSchoolName: string;
    status: string;
    statusProgress?: string | null;
}

interface CleverMap { [key: string]: boolean; }

const INIT_DATA = "initData";
const PILOT_STATUS = "pilotStatus";
const STAFF_IMPORT_STATUS = "staffImportStatus";

const MATCH_PROGRESS_ENUM = {
    TEST_MATCH_DONE: "Matched, test import complete",
    TEST_MATCH_IN_PROGRESS: "Matched, test import in progress",
    PENDING_TEST_MATCH:  "Matched (test import not started)",
    IMPORT_IN_PROGRESS: "Import in progress...",
    IMPORT_COMPLETE: "Imported"
};

export class CleverMatchLandingController {
    navianceSchools: NavianceSchoolWithProducts[];
    matches: Match[];
    navianceSchoolWithCleverMap: CleverMap;
    navianceSchoolsNameWithStatus: NavianceSchoolWithNameAndStatus[] = [];
    columns: Column[];
    pilotStatus: boolean = false;
    staffImportStatus: StaffImportStatus;

    /* @ngInject */
    constructor(
        public $route: angular.route.IRouteService,
        private $location: angular.ILocationService,
        private $routeParams: { districtId: string },
    ) {}

    $onInit() {
        const initData = this.$route.current.locals[INIT_DATA];

        if (this.$route.current.locals[PILOT_STATUS] === 200) {
            this.pilotStatus = true;
        }

        this.navianceSchools = initData.navianceSchools;
        this.matches = initData.matches.map(match => {
            match.statusDisplay = match.status ? MATCH_PROGRESS_ENUM[match.status] : "Matched";
            return match;
        });

        this.navianceSchoolWithCleverMap = this.getNavianceSchoolsCleverMap();
        this.navianceSchoolsNameWithStatus = this.getNavianceSchoolNameWithStatus();

        this.columns = [
            {headerName: "School", field: "navianceSchoolName", initialSort: "ASC", cellClass: "ag-cell--vertical-center"},
            {headerName: "Import Status with Clever", field: "status", cellRenderer: this.renderHtmlForSchoolStatus}
        ];

        this.staffImportStatus = this.$route.current.locals[STAFF_IMPORT_STATUS];
    }

    private getNavianceSchoolsCleverMap = (): CleverMap => {
        if (this.navianceSchools == null) { return {}; }

        return this.navianceSchools.reduce((acc, nxt) => {
            acc[nxt.navianceSchool.institutionUuid] = true;
            return acc;
        }, {});
    }

    private renderHtmlForSchoolStatus = (params) => {
        params.$scope.vm = this;
        return `
            <div class="ag-cell--vertical-center" ng-if="vm.isStatusNoCleverSubscription(data)">
                {{ data.status }}
            </div>
            <span ng-if="vm.isStatusUnmatched(data)">
                <span class="status-icon status-icon__unmatched">
                    <i class="ns-icon ns-icon--size-large ns-icon--unlink"></i>
                </span>
                {{ data.status }}
            </span>
            <span ng-if="vm.isStatusMatched(data)">
                <div class="match-icon">
                    <span class="status-icon status-icon__matched">
                        <i class="ns-icon ns-icon--size-large ns-icon--link"></i>
                    </span>
                </div>
                {{ data.statusProgress }}
            </span>
            <span ng-if="vm.isStatusImported(data)">
                <span class="status-icon status-icon__imported">
                    <i class="ns-icon ns-icon--size-large ns-icon--building-filled"></i>
                </span>
                {{ data.statusProgress }}
            </span>
        `;
    }

    getNavianceSchoolNameWithStatus(): NavianceSchoolWithNameAndStatus[] {
        if (!this.navianceSchools) { return []; }

        const notClever = [];

        const haveClever = this.navianceSchools.map(s => ({
            navianceSchoolName: s.navianceSchool.institutionName,
            status: this.isSchoolMatched(s) ? "Matched" : "Unmatched",
            statusProgress: this.getMatchedSchoolStatus(s)
        }));

        return [...notClever, ...haveClever];
    }

    isStatusNoCleverSubscription(school) : boolean {
        return school.status === "No active Naviance Integration Subscription";
    }

    isStatusMatched(school): boolean {
        return !!school.statusProgress && school.statusProgress.indexOf("Match") === 0;
    }

    isStatusImported(school): boolean {
        return school.statusProgress === "Import in progress..." || school.statusProgress === "Imported";
    }

    isStatusUnmatched(navianceSchool): boolean { return navianceSchool.status === "Unmatched"; }

    isMatchAvailable(): boolean {
        return !!this.matches && this.matches.length > 0 && this.isMatchedSchoolSubscribed();
    }

    haveCleverSubscription(): boolean {
        if (!this.navianceSchools) { return false; }
        return true;
    }

    private isSchoolMatched(school): boolean {
        return !!this.matches && this.matches.some(m => m.navianceId === school.navianceSchool.institutionUuid);
    }

    private getMatchedSchoolStatus(school): string | null {
        if (!this.isSchoolMatched(school)) { return null; }
        return this.matches.filter(m => m.navianceId === school.navianceSchool.institutionUuid)[0].statusDisplay;
    }

    isSavedMatchAvailable(): boolean { return !!this.matches && this.matches.length > 0; }

    previewMatches() {
        return this.$location.path(`${this.$routeParams.districtId}/preview`);
    }

    viewTestImport() {
        return this.$location.path(`${this.$routeParams.districtId}/student-test-import`);
    }

    isPageThreeReady() {
        if (!this.matches) { return false; }
        return this.matches.filter(
            m => m.status === "TEST_MATCH_DONE" || m.status === "IMPORT_IN_PROGRESS" || m.status === "IMPORT_COMPLETE"
        ).length > 0;
    }

    private isMatchedSchoolSubscribed(): boolean {
        const subscribed = this.matches.filter(m => this.navianceSchoolWithCleverMap[m.navianceId]);
        return subscribed && subscribed.length > 0;
    }

    getListOfSchoolsForImportedStaffs() {
        if (!this.navianceSchoolsNameWithStatus) { return; }

        let schools = [];

        this.navianceSchoolsNameWithStatus.filter(school => {
            if (school.statusProgress === "Imported") {
                schools.push(school.navianceSchoolName);
            }
        });

        return schools;
    }

    isVisible():boolean {
        return this.pilotStatus;
    }

    goToStaffImport() {
        if (!(this.staffImportStatus && this.staffImportStatus.startedOn)) {
            return this.$location.path(`${this.$routeParams.districtId}/staff-test-import`);
        }

        return this.$location.path(`${this.$routeParams.districtId}/staff-import-records`);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/clever-match-landing/clever-match-landing.controller.ts